import { useTranslation } from 'react-i18next'
import type { RequestDocs, RequestParamDoc } from '@/api/endpoints'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function hasRequestDocsContent(docs: RequestDocs): boolean {
  return (
    docs.headers.length > 0 ||
    docs.query.length > 0 ||
    docs.path_params.length > 0 ||
    !!docs.body_example ||
    !!docs.body_description ||
    docs.errors.length > 0
  )
}

function ParamDocTable({ title, rows }: { title: string; rows: RequestParamDoc[] }) {
  const { t } = useTranslation()
  if (rows.length === 0) return null

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{title}</p>
      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/4">{t('endpoint.requestDocs.colKey')}</TableHead>
              <TableHead className="w-1/4">{t('endpoint.requestDocs.colExample')}</TableHead>
              <TableHead>{t('endpoint.requestDocs.colDescription')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell className="font-mono text-xs font-medium align-top">{r.key}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground align-top break-all">{r.example}</TableCell>
                <TableCell className="text-xs align-top">{r.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

interface RequestDocsViewProps {
  requestDocs: RequestDocs
}

export function RequestDocsView({ requestDocs }: RequestDocsViewProps) {
  const { t } = useTranslation()

  if (!hasRequestDocsContent(requestDocs)) return null

  return (
    <div className="space-y-4">
      <ParamDocTable title={t('endpoint.requestDocs.headersTitle')} rows={requestDocs.headers} />
      <ParamDocTable title={t('endpoint.requestDocs.queryTitle')} rows={requestDocs.query} />
      <ParamDocTable title={t('endpoint.requestDocs.pathParamsTitle')} rows={requestDocs.path_params} />

      {(requestDocs.body_example || requestDocs.body_description) && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {t('endpoint.requestDocs.bodyTitle')}
          </p>
          {requestDocs.body_description && (
            <p className="text-xs text-muted-foreground">{requestDocs.body_description}</p>
          )}
          {requestDocs.body_example && (
            <pre className="rounded-md border bg-muted/30 p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
              {requestDocs.body_example}
            </pre>
          )}
        </div>
      )}

      {requestDocs.errors.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {t('endpoint.requestDocs.errorsTitle')}
          </p>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">{t('endpoint.requestDocs.colStatus')}</TableHead>
                  <TableHead>{t('endpoint.requestDocs.colDescription')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requestDocs.errors.map((e, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-xs font-semibold align-top">{e.status}</TableCell>
                    <TableCell className="text-xs align-top">{e.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  )
}
